/**
 * Single source of truth for per-page SEO metadata.
 * Consumed by:
 *   - useSeo hook (client-side hydration + navigation)
 *   - scripts/prerender.js (build-time static HTML generation)
 *
 * Written as CommonJS so the Node prerender script can require() it
 * directly. CRA/webpack transparently supports CJS in React imports.
 */

const LOGO_URL = 'https://powerlab.works/brand/logo.png';
const BASE = 'https://powerlab.works';

const izmirAutoRepairSchema = (url, lang) => ({
  '@context': 'https://schema.org',
  '@type': 'AutoRepair',
  name: 'Powerlab Works HQ Türkiye',
  image: LOGO_URL,
  url,
  telephone: '+90 532 786 66 35',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Kazımdirik Mahallesi 283. Sokak No:3',
    addressLocality: 'Bornova',
    addressRegion: 'İzmir',
    addressCountry: 'TR',
  },
  areaServed: [{ '@type': 'City', name: lang === 'tr' ? 'İzmir' : 'Izmir' }],
  sameAs: [`${BASE}/`],
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '09:00',
    closes: '19:00',
  }],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: lang === 'tr' ? 'Chip Tuning Hizmetleri' : 'Chip Tuning Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: lang === 'tr' ? 'Stage 1 / 2 / 3 ECU Yazılım' : 'Stage 1 / 2 / 3 ECU Remapping' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: lang === 'tr' ? 'DPF / EGR / AdBlue Çözümleri' : 'DPF / EGR / AdBlue Solutions' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: lang === 'tr' ? 'E85 Bioetanol Dönüşüm' : 'E85 Bioethanol Conversion' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: lang === 'tr' ? 'Dyno Test' : 'Dyno Testing' } },
    ],
  },
});

// FAQ content - shared between JSX render and FAQPage JSON-LD so
// AI models see the same text on-page and in structured data.
const faqIzmirEn = [
  {
    q: "What's the best chip tuning company in Izmir?",
    a: "Powerlab Works HQ Türkiye in Bornova, Izmir, is part of Powerlab Works OU (Estonia-registered 2019, actively tuning since 2012). The group runs a 40-plus dealer network across 20-plus countries and has processed over 300,000 vehicle files. Every remap is dyno-verified and fully reversible.",
  },
  {
    q: "How much does ECU remapping cost in Izmir?",
    a: "ECU remapping prices vary based on your vehicle's brand, model, year, and the scope of work (Stage 1/2/3, DPF/EGR/AdBlue, E85 conversion). Powerlab Works HQ Türkiye in Bornova provides a tailored quote based on your vehicle's details — get a quote within minutes via WhatsApp at +90 532 786 66 35.",
  },
  {
    q: "Is chip tuning safe for my engine?",
    a: "Yes, when done correctly. Powerlab Works tunes each vehicle with a dedicated file — no copy/paste — and every remap stays within safe boost, EGT and injection limits verified on our chassis dyno. All changes are fully reversible; we keep a backup of your original ECU file for restoration.",
  },
  {
    q: "How long does a Stage 1 remap take?",
    a: "A typical Stage 1 ECU remap at Powerlab Works HQ Türkiye in Bornova, Izmir takes 2 to 4 hours. This includes OBD reading of the original file, vehicle-specific calibration, writing back to the ECU and a road or dyno verification. Complex bench-only platforms may require additional time.",
  },
  {
    q: "Do you offer dyno testing in Izmir?",
    a: "Yes. Our Bornova workshop uses a chassis dyno to measure stock horsepower and torque, verify Stage 1 gains and confirm the remap stays within safe operating limits. Every tune is documented with a before/after graph so you see the actual gain, not a database estimate.",
  },
];

const faqIzmirTr = [
  {
    q: "İzmir'de en iyi chip tuning firması hangisi?",
    a: "İzmir Bornova'daki Powerlab Works HQ Türkiye, Powerlab Works OU'nun (Estonya'da 2019 resmi kuruluş, 2012'den beri aktif tuning) Türkiye ofisidir. Grup 20'den fazla ülkede 40'ı aşkın bayi ağı ile çalışır ve bugüne kadar 300.000'in üzerinde araç dosyası işlemiştir. Her ECU yazılımı chassis dyno cihazımızda doğrulanır ve tamamen geri alınabilirdir.",
  },
  {
    q: "İzmir'de ECU yazılım fiyatları ne kadar?",
    a: "ECU yazılım fiyatları aracın marka, model, yılı ve yapılacak işin kapsamına (Stage 1/2/3, DPF/EGR/AdBlue, E85 dönüşüm) göre değişir. Bornova'daki Powerlab Works HQ Türkiye, aracınızın bilgilerine göre size özel net fiyat verir — +90 532 786 66 35 numaralı WhatsApp üzerinden birkaç dakika içinde teklif alabilirsiniz.",
  },
  {
    q: "Chip tuning aracıma zarar verir mi?",
    a: "Doğru yapıldığında hayır. Powerlab Works her araca özel yazılım hazırlar; copy/paste yapmayız. Turbo basıncı, egzoz sıcaklığı ve enjeksiyon değerleri güvenli sınırlar içinde kalır ve chassis dyno cihazımızda doğrulanır. Orijinal ECU dosyanızın yedeğini alırız; istediğiniz zaman geri döndürülebilir, servis geçmişi etkilenmez.",
  },
  {
    q: "Stage 1 yazılım ne kadar sürer?",
    a: "İzmir Bornova'daki Powerlab Works HQ Türkiye'de tipik bir Stage 1 ECU yazılımı 2 ile 4 saat sürer. Bu süre OBD üzerinden orijinal dosyayı okuma, araca özel kalibrasyon, ECU'ya geri yazma ve yol veya dyno testini kapsar. Karmaşık, sadece bench üzerinden okunan platformlarda ek süre gerekebilir.",
  },
  {
    q: "İzmir'de dyno test yapıyor musunuz?",
    a: "Evet. Bornova'daki atölyemizde chassis dyno kullanarak orijinal beygir ve tork değerlerini ölçüyor, Stage 1 kazançlarını doğruluyor ve yazılımın güvenli sınırlar içinde kaldığını teyit ediyoruz. Her tuning için önce/sonra grafik dokümante edilir; veritabanı tahmini değil, gerçek ölçülmüş kazanç görürsünüz.",
  },
];

const faqPageSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

const seo = {
  home: {
    path: '/',
    title: 'Powerlab Works — Professional Chip Tuning & ECU Remapping',
    description: 'Unlock your vehicle\'s true potential. Professional chip tuning and ECU remapping — 40+ dealers across 20+ countries, 300,000+ processed vehicle files, active since 2012.',
    canonical: `${BASE}/`,
    ogImage: LOGO_URL,
    lang: 'en',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Powerlab Works',
      legalName: 'Powerlab Works OU',
      url: BASE,
      logo: LOGO_URL,
      foundingDate: '2019',
      foundingLocation: 'Tallinn, Estonia',
      sameAs: [],
      contactPoint: [{
        '@type': 'ContactPoint',
        telephone: '+90 532 786 66 35',
        contactType: 'customer service',
        email: 'info@powerlab.works',
      }],
    },
  },
  calculator: {
    path: '/calculator',
    title: 'Power Calculator | Powerlab Works',
    description: 'Free chip tuning power calculator — real Stage 1 gain estimates from our database of 14,210 engines and 13,164 performance records. Discover your vehicle\'s true potential in seconds.',
    canonical: `${BASE}/calculator`,
    ogImage: LOGO_URL,
    lang: 'en',
  },
  faq: {
    path: '/faq',
    title: 'Frequently Asked Questions | Powerlab Works',
    description: 'Answers to common questions about chip tuning, ECU remapping, warranty, safety and pricing at Powerlab Works.',
    canonical: `${BASE}/faq`,
    ogImage: LOGO_URL,
    lang: 'en',
  },
  izmirEn: {
    path: '/izmir-chip-tuning',
    title: 'Izmir Chip Tuning & ECU Software Center | Powerlab Works',
    description: 'Professional chip tuning and ECU remapping in Izmir, Turkey. 40+ dealer network across 20+ countries, 300,000+ processed vehicle files, active since 2012. Book your appointment today.',
    canonical: `${BASE}/izmir-chip-tuning`,
    ogImage: LOGO_URL,
    lang: 'en',
    hreflang: [
      { lang: 'en', href: `${BASE}/izmir-chip-tuning` },
      { lang: 'tr', href: `${BASE}/tr/izmir-chip-tuning` },
      { lang: 'x-default', href: `${BASE}/izmir-chip-tuning` },
    ],
    jsonLd: [
      izmirAutoRepairSchema(`${BASE}/izmir-chip-tuning`, 'en'),
      faqPageSchema(faqIzmirEn),
    ],
  },
  izmirTr: {
    path: '/tr/izmir-chip-tuning',
    title: 'İzmir Chip Tuning & ECU Yazılım Merkezi | Powerlab Works',
    description: 'İzmir Bornova\'da profesyonel chip tuning ve ECU yazılım hizmeti. 20+ ülkede 40+ bayi, 300.000+ işlenmiş araç dosyası, 2012\'den beri aktif. Randevu için hemen iletişime geçin.',
    canonical: `${BASE}/tr/izmir-chip-tuning`,
    ogImage: LOGO_URL,
    lang: 'tr',
    hreflang: [
      { lang: 'en', href: `${BASE}/izmir-chip-tuning` },
      { lang: 'tr', href: `${BASE}/tr/izmir-chip-tuning` },
      { lang: 'x-default', href: `${BASE}/izmir-chip-tuning` },
    ],
    jsonLd: [
      izmirAutoRepairSchema(`${BASE}/tr/izmir-chip-tuning`, 'tr'),
      faqPageSchema(faqIzmirTr),
    ],
  },
  privacyTr: {
    path: '/tr/gizlilik',
    title: 'Gizlilik ve Çerez Politikası | Powerlab Works',
    description: 'Powerlab Works kişisel verilerinizi KVKK ve GDPR kapsamında nasıl işler: hangi veriler, hangi amaçla, kimlerle paylaşılır, ne kadar saklanır, çerezler ve haklarınız.',
    canonical: `${BASE}/tr/gizlilik`,
    ogImage: LOGO_URL,
    lang: 'tr',
    hreflang: [
      { lang: 'en', href: `${BASE}/en/privacy` },
      { lang: 'tr', href: `${BASE}/tr/gizlilik` },
      { lang: 'x-default', href: `${BASE}/en/privacy` },
    ],
  },
  privacyEn: {
    path: '/en/privacy',
    title: 'Privacy & Cookie Policy | Powerlab Works',
    description: 'How Powerlab Works processes your personal data under GDPR and KVKK: what we collect, why, who we share it with, how long we keep it, cookies and your rights.',
    canonical: `${BASE}/en/privacy`,
    ogImage: LOGO_URL,
    lang: 'en',
    hreflang: [
      { lang: 'en', href: `${BASE}/en/privacy` },
      { lang: 'tr', href: `${BASE}/tr/gizlilik` },
      { lang: 'x-default', href: `${BASE}/en/privacy` },
    ],
  },
};

const LEGACY_REDIRECTS = [
  { from: '/home', to: '/' },
  { from: '/blog', to: '/' },
  { from: '/parts', to: '/calculator' },
  { from: '/parts/stage3', to: '/calculator' },
  { from: '/tr/home', to: '/tr/izmir-chip-tuning' },
  { from: '/tr/anasayfa', to: '/tr/izmir-chip-tuning' },
  { from: '/tr', to: '/tr/izmir-chip-tuning' },
  { from: '/privacy', to: '/en/privacy' },
];

module.exports = { seo, BASE, LOGO_URL, faqIzmirEn, faqIzmirTr, LEGACY_REDIRECTS };
